import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/auth/useProfile'; 
import { toast } from 'react-hot-toast';

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isCareerToolsOpen, setIsCareerToolsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, signOut } = useAuth();
  const { profile } = useProfile();
  const { isAdmin } = useAuth();

  // Add scroll event listener
  React.useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut();
      toast.success('Signed out successfully');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to sign out');
    }
  };

  const isAuthPage = ['/login', '/create-account', '/'].includes(location.pathname);
  if (isAuthPage || location.pathname === '/') return null;

  return (
    <header className="fixed w-full top-0 z-50 transition-all duration-300 bg-white shadow-sm">
      <nav className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Left side - Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center w-auto">
              <img 
                src="https://i.ibb.co/0Vqpgqkq/THE-TECH-FIRM-COMPANY-1.png"
                alt="TEZHLY"
                className="h-12 w-auto object-contain"
                onError={(e) => {
                  // Fallback to text if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.insertAdjacentHTML('afterend', '<span class="text-2xl font-bold text-[#800020]">TEZHLY</span>');
                }}
              />
            </Link>
          </div>

          {/* Center Navigation */}
          <div className="hidden md:flex items-center">
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <div className="rounded-full px-16 py-3.5 bg-[#f4f4f4]/30">
                <div className="flex items-center space-x-12">
                  <Link to="/prestige-club" className="text-[#000000] font-medium hover:text-[#a30028]" style={{ fontFamily: 'Times New Roman', textTransform: 'capitalize' }}>
                    Dashboard
                  </Link><Link to="/community" className="text-[#000000] font-medium hover:text-[#a30028]" style={{ fontFamily: 'Times New Roman', textTransform: 'capitalize' }}>
                    Community
                  </Link>
                  <Link to="/events" className="text-[#000000] font-medium hover:text-[#a30028]" style={{ fontFamily: 'Times New Roman', textTransform: 'capitalize' }}>
                    Events
                  </Link>
                  <Link to="/blog" className="text-[#000000] font-medium hover:text-[#a30028]" style={{ fontFamily: 'Times New Roman', textTransform: 'capitalize' }}>
                    Blog
                  </Link>
                  
                  <div className="relative group">
                    <button 
                      className="text-[#000000] font-medium hover:text-[#a30028] flex items-center"
                      style={{ fontFamily: 'Times New Roman', textTransform: 'capitalize' }}
                    >
                      Career Lab
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <div className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-48 bg-white rounded-xl shadow-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                      <Link 
                        to="/prestige-club" 
                        className="block px-4 py-2 text-sm text-[#000000] hover:bg-gray-100"
                        style={{ fontFamily: 'Times New Roman', textTransform: 'capitalize' }}
                      >
                        My Dashboard
                      </Link>
                      <Link 
                        to="/jobs" 
                        className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-100"
                        style={{ fontFamily: 'Times New Roman', textTransform: 'capitalize' }}
                      >
                        Private Job Board
                      </Link>
                      <Link 
                        to="/courses/marketplace" 
                        className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-100"
                        style={{ fontFamily: 'Times New Roman', textTransform: 'capitalize' }}
                      >
                        Course Market
                      </Link>
                      <Link 
                        to="/career-chat" 
                        className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-100"
                        style={{ fontFamily: 'Times New Roman', textTransform: 'capitalize' }}
                      >
                        My Career Coach
                      </Link>
                      <Link 
                        to="/resume-review" 
                        className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-100"
                        style={{ fontFamily: 'Times New Roman', textTransform: 'capitalize' }}
                      >
                        My Resume
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center">
            {isAuthenticated ? (
              <div className="relative">
                <div className="flex items-center space-x-4">
                  <button 
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className="text-[#800020] font-medium hover:text-[#a30028] text-sm flex items-center"
                  >
                    {profile?.first_name || 'Profile'}
                    <Menu className="h-5 w-5 ml-2" />
                  </button>
                </div>
                
                {isMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg py-1 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-black">
                        {profile?.first_name} {profile?.last_name}
                      </p>
                      <p className="text-xs text-gray-500">{profile?.email}</p>
                    </div>
                    {/* Career Lab Submenu */}
                    <div className="relative group">
                      <button 
                        className="w-full text-left px-4 py-2 text-sm text-[#800020] hover:bg-gray-100 flex items-center justify-between"
                        onClick={() => setIsCareerToolsOpen(!isCareerToolsOpen)}
                      >
                        Career Lab
                        <ChevronRight className="h-4 w-4" />
                      </button>
                      {isCareerToolsOpen && (
                        <div className="absolute right-full top-0 w-48 bg-white rounded-xl shadow-lg py-1">
                          <Link
                            to="/jobs"
                            className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-100"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            Jobs
                          </Link>
                          <Link
                            to="/courses/marketplace"
                            className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-100"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            Courses
                          </Link>
                          <Link
                            to="/career-chat"
                            className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-100"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            Career Coach
                          </Link>
                          <Link
                            to="/resume-review"
                            className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-100"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            My Resume
                          </Link>
                          <Link
                            to="/admin/layoffs"
                            className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-100"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            Live Layoff Tracker
                          </Link>
                        </div>
                      )}
                    </div>

                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-100"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      My Profile
                    </Link>
                    <Link
                      to="/events"
                      className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-100"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Events
                    </Link><Link
                      to="/blog"
                      className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-100"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Blog
                    </Link>

                    <div className="border-t border-gray-100 my-1"></div>

                    {profile?.is_admin && (
                      <>
                        <Link
                          to="/admin"
                          className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-100"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          Admin Dashboard
                        </Link>
                       
                      </>
                    )}

                    <Link
                      to="/contact"
                      className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-100"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Contact
                    </Link>

                    <Link
                      to="/business"
                      className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-100"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Partners
                    </Link>

                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-[#800020] hover:bg-gray-100 flex items-center"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <button 
                  onClick={() => navigate('/login')}
                  className="text-[#800020] font-medium hover:text-[#a30028] mr-4 text-sm"
                >
                  Sign In
                </button>
                <div className="relative">
                  <button
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className="ml-4 text-[#800020] hover:text-[#a30028] transition-colors relative"
                  >
                    <Menu className="h-6 w-6" />
                  </button>
                  
                  {isMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-100 font-medium border-b border-gray-100"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        User Profile
                      </Link>
                      <Link
                        to="/subscription"
                        className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-100"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Upgrade to Premium
                      </Link>
                      <Link
                        to="/community"
                        className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-50"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Community
                      </Link>
                      <Link
                        to="/jobs"
                        className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-50"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Jobs
                      </Link>
                      <Link
                        to="/courses/marketplace"
                        className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-50"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Courses
                      </Link>
                      <Link 
                        to="/career-chat"
                        className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-50"
                        onClick={() => setIsMenuOpen(false)} 
                      >
                        Career Coach
                      </Link>
                      <Link
                        to="/resume-review"
                        className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-50"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        My Resume
                      </Link>
                      <Link
                        to="/admin/layoffs"
                        className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-100"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Live Layoff Tracker
                      </Link>
                     <Link
                       to="/business"
                       className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-100"
                       onClick={() => setIsMenuOpen(false)}
                     >
                       For Businesses
                     </Link>
                      <Link
                        to="/contact"
                        className="block px-4 py-2 text-sm text-[#800020] hover:bg-gray-100"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Contact
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-3 py-2 text-red-600 hover:bg-gray-50 flex items-center"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <div className="flex items-center space-x-4">
              <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-[#800020]"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className={`md:hidden py-4 border-t border-gray-100 transition-all duration-300 ${
          isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        } ${!isScrolled ? 'bg-white' : 'bg-transparent'}`}>
            <div className="space-y-2">
              <Link
                to="/prestige-club"
                className="block px-3 py-2 text-[#800020] hover:bg-gray-50"
                onClick={() => setIsMenuOpen(false)}
              >
                My Dashboard
              </Link><Link
                to="/community"
                className="block px-3 py-2 text-[#800020] hover:bg-gray-50"
                onClick={() => setIsMenuOpen(false)}
              >
                Community
              </Link>
              <Link
                to="/events"
                className="block px-3 py-2 text-[#800020] hover:bg-gray-50"
                onClick={() => setIsMenuOpen(false)}
              >
                Events
              </Link>
              <Link
                to="/blog"
                className="block px-3 py-2 text-[#800020] hover:bg-gray-50"
                onClick={() => setIsMenuOpen(false)}
              >
                Blog
              </Link>
              <Link
                to="/profile"
                className="block px-3 py-2 text-[#800020] hover:bg-gray-50 border-b border-gray-100"
                onClick={() => setIsMenuOpen(false)}
              >
                My Profile
              </Link>
              {/* Career Lab Section */}
              <div className="border-b border-gray-100">
                <button
                  onClick={() => setIsCareerToolsOpen(!isCareerToolsOpen)}
                  className="w-full text-left px-3 py-2 text-[#800020] hover:bg-gray-50 flex items-center justify-between"
                >
                  Career Lab
                  <ChevronRight className={`h-4 w-4 transition-transform ${isCareerToolsOpen ? 'rotate-90' : ''}`} />
                </button>
                {isCareerToolsOpen && (
                  <div className="pl-6 space-y-2 bg-gray-50">
                    <Link
                      to="/jobs"
                      className="block px-3 py-2 text-[#800020] hover:bg-gray-100"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Jobs
                    </Link>
                    <Link
                      to="/prestige-club"
                      className="block px-3 py-2 text-[#800020] hover:bg-gray-100"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      My Dashboard
                    </Link>
                    <Link
                      to="/courses/marketplace"
                      className="block px-3 py-2 text-[#800020] hover:bg-gray-100"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Courses
                    </Link>
                    <Link
                      to="/career-chat"
                      className="block px-3 py-2 text-[#800020] hover:bg-gray-100"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Career Coach
                    </Link>
                    <Link
                      to="/resume-review"
                      className="block px-3 py-2 text-[#800020] hover:bg-gray-100"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      My Resume
                    </Link>
                    <Link
                      to="/admin/layoffs"
                      className="block px-3 py-2 text-[#800020] hover:bg-gray-100"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Live Layoff Tracker
                    </Link>
                  </div>
                )}
              </div>

              {/* Contact and Partners */}
              <Link
                to="/contact"
                className="block px-3 py-2 text-[#800020] hover:bg-gray-50"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>
              <Link
                to="/business"
                className="block px-3 py-2 text-[#800020] hover:bg-gray-50"
                onClick={() => setIsMenuOpen(false)}
              >
                Partners
              </Link>

              <button
                onClick={handleLogout}
                className="block w-full text-left px-3 py-2 text-red-600 hover:bg-gray-50 flex items-center"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}

export { Header }